import { useState, useEffect, useRef, useCallback } from 'react';
import { io } from 'socket.io-client';

/**
 * Custom hook for connecting to the /training Socket.io namespace.
 * Manages connection lifecycle and provides session join/leave methods.
 */
function useTrainingSocket(token) {
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const socketRef = useRef(null);

  useEffect(() => {
    if (!token) {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
        setSocket(null);
        setIsConnected(false);
      }
      return;
    }

    let socketURL = process.env.REACT_APP_API_URL || 'http://localhost:8000';
    if (socketURL.endsWith('/api')) {
      socketURL = socketURL.substring(0, socketURL.length - 4);
    }

    const newSocket = io(`${socketURL}/training`, {
      auth: { token },
      transports: ['websocket'],
    });

    newSocket.on('connect', () => {
      console.log(`[TrainingSocket] Connected: ${newSocket.id}`);
      setIsConnected(true);
      setSocket(newSocket);
    });

    newSocket.on('disconnect', (reason) => {
      console.log('[TrainingSocket] Disconnected:', reason);
      setIsConnected(false);
      if (reason === 'io server disconnect') {
        newSocket.connect();
      }
    });

    newSocket.on('connect_error', (err) => {
      console.error('[TrainingSocket] Connection error:', err.message);
      setIsConnected(false);
    });

    socketRef.current = newSocket;

    return () => {
      console.log('[TrainingSocket] Cleaning up socket connection');
      newSocket.disconnect();
      socketRef.current = null;
      setSocket(null);
      setIsConnected(false);
    };
  }, [token]);

  const joinSession = useCallback((sessionId) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('training:join_session', { sessionId });
    }
  }, []);

  const leaveSession = useCallback((sessionId) => {
    if (socketRef.current && socketRef.current.connected) {
      socketRef.current.emit('training:leave_session', { sessionId });
    }
  }, []);

  return { socket, isConnected, joinSession, leaveSession };
}

export default useTrainingSocket;
